import React, { useState, useMemo, useEffect } from 'react';
import styles from './InputDate.module.scss';

/* Translation */
import { tr } from '../../../utils/translations/translator';
import { useTranslation } from 'react-i18next';

/* Components */
import { InputHeader } from '../shared/InputHeader/InputHeader';
import { InputDateSelection } from './InputDateSelection';

/* Utils */
import { buildCustomDate } from '../utils/buildCustomDate';
import {
  getNextDate,
  getPreviousDate,
} from '../utils/dateTimeNavigationFunctions';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';

interface InputDateProps {
  closeModal: () => void;
  handleChange: (date: string) => void;
  value: string;
}

export const InputDate: React.FC<InputDateProps> = ({
  closeModal,
  handleChange,
  value,
}) => {
  const { t } = useTranslation();

  const [date, setDate] = useState<Date>(new Date(value));
  const [showError, setShowError] = useState<boolean>(false);
  const currentDate = useMemo(() => new Date(), []);
  // const [hour, setHour] = useState<string | undefined>(
  //   new Date(value).getHours().toString(),
  // );
  // const [minutes, setMinutes] = useState<string | undefined>(
  //   new Date(value).getMinutes().toString(),
  // );
  const [isCurrentDay, setIsCurrentDay] = useState<boolean>(true);

  useMemo(() => {
    if (buildCustomDate(date) === buildCustomDate(currentDate)) {
      setIsCurrentDay(true);
    } else {
      setIsCurrentDay(false);
    }
  }, [date, currentDate]);

  useEffect(() => {
    if (moment(date) <= moment(currentDate)) {
      handleChange(currentDate.toISOString());
      return;
    } else {
      setShowError(false);
      const newDate = new Date(date);
      handleChange(newDate.toISOString());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const formatDate = useMemo((): React.ReactNode => {
    const [dayNum, month] = buildCustomDate(date).split(' ');
    return (
      <span>
        {dayNum} <br /> {month}
      </span>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const formatPreviousDate = useMemo((): React.ReactNode | undefined => {
    if (!isCurrentDay) {
      const previousDate = getPreviousDate(date);
      const [dayNum, month] = buildCustomDate(previousDate).split(' ');
      return (
        <span>
          {dayNum} <br /> {month}
        </span>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, isCurrentDay]);

  const formatNextDate = useMemo((): React.ReactNode => {
    const clonedDate = new Date(JSON.parse(JSON.stringify(date)));
    const nextDate = new Date(clonedDate.setDate(clonedDate.getDate() + 1));
    const [dayNum, month] = buildCustomDate(nextDate).split(' ');
    return (
      <span>
        {dayNum} <br /> {month}
      </span>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const handleNextDate = () => {
    const nextDate = getNextDate(date);
    setDate(nextDate);
  };
  const handlePreviousDate = () => {
    const previousDate = getPreviousDate(date);
    setDate(previousDate);
    checkTimeErrors(previousDate);
  };

  const handleTimeChange = (value: Moment) => {
    if (value.isValid()) {
      setDate(new Date(moment(value).format()));
    }
  };
  const checkTimeErrors = (value?: Date) => {
    if (moment(value || date) <= moment(currentDate)) {
      setShowError(true);
      return;
    }
    return;
  };

  return (
    <div className={styles.InputDate}>
      <div className={styles.topRow}>
        <span className={styles.text}>{tr(t, 'select-date')}</span>
        <button
          onClick={() => closeModal()}
          className={styles.okay}
          disabled={moment(date) <= moment(currentDate)}
          id={moment(date) <= moment(currentDate) ? styles.error : styles.unset}
        >
          OK
        </button>
      </div>
      <InputHeader title={tr(t, 'hour')} />
      <div
        className={styles.timeInputWrapper}
        id={showError ? styles.errorUnderline : styles.unsetUnderline}
      >
        <div className={showError ? styles.error : styles.unset}>
          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="de">
            <TimeField
              value={moment(date)}
              onChange={(newValue) => newValue && handleTimeChange(newValue)}
              ampm={false}
              onBlur={() => checkTimeErrors()}
              inputProps={{
                typeof: 'date',
                autoComplete: 'new-password',
                form: {
                  autoComplete: 'off',
                },
              }}
              autoFocus={true}
            />
          </LocalizationProvider>
        </div>
      </div>
      <InputHeader title={tr(t, 'day-month')} />
      <InputDateSelection
        value={formatDate}
        previous={formatPreviousDate}
        next={formatNextDate}
        handleNext={handleNextDate}
        handlePrevious={handlePreviousDate}
      />
    </div>
  );
};
